import type {
  Block, Cell, ChipCell, Column, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Onboarding › SLA breaches, read from `GET /v1/admin/onboarding/sla` and
 * `GET /v1/admin/onboarding/sla/cohorts` (go-wallet#84).
 *
 * # Why the screen takes two reads and not one
 *
 * The queue answers "who is stuck right now"; the cohorts answer "are we
 * getting faster or slower". A breach queue on its own can be worked every day
 * and still be growing, and the trend is the only thing on the screen that says
 * so. They are two endpoints because they are two questions with different
 * costs, not because they are two screens.
 *
 * The consequence is the part that has to be got right: **a screen where one of
 * the two reads failed is `partial`, and it names which.** A failed cohorts read
 * that rendered an empty trend table would say "no application has reached a
 * verdict in a year", which is a much worse sentence than "we could not read
 * it". So the cohort half degrades to a note that says so, in place of the
 * table, and the screen stops claiming `live`.
 *
 * The queue is the screen, though, so a failed queue read collapses everything:
 * one note and nothing else. A trend rendered above nothing reads as a working
 * screen with a quiet day behind it.
 *
 * # What this endpoint does not send, and what follows
 *
 * Unlike `/dashboard/today`, neither route carries a provenance block, a
 * server-side read time, or an availability per figure. Three consequences:
 *
 *   - The freshness stamp is the instant the CONSOLE received the answer, and it
 *     is labelled as that rather than passed off as a read time. `at` is a
 *     parameter for the same reason the backend's decision functions take `now`
 *     — no ambient clock in a pure function.
 *   - Authority is asserted from the route (`operational`, Postgres `customers`)
 *     rather than read off the payload. Worth an issue on go-wallet; not worth
 *     inventing a field here.
 *   - Absence has to be inferred from the field being off the wire, so every
 *     read below distinguishes "absent", "present and unusable" and "present".
 *     A `0` is never substituted for any of them: `in_state_seconds` is a real
 *     zero for someone who just entered a state, and `—` is what an unread one
 *     looks like. They must not render alike.
 *
 * There is also no customer NAME, no owner and no escalation flag on the wire,
 * which the design corpus draws columns for. Those columns are corpus data and
 * are simply not here — an invented "unassigned" would be a claim about a
 * process we do not have.
 *
 * # Nothing on this screen is money, and nothing on it approves anything
 *
 * No amount, no currency, no balance — so no `formatMinor` and no amount cell.
 * And every route here is a read: only the institution accepts or rejects, so a
 * promote/approve affordance would be a verdict we are not licensed to give.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_sla.go json tags
// ---------------------------------------------------------------------------

/**
 * One queue row.
 *
 * Every field is optional here even though Go emits most of them
 * unconditionally, because the parse below trusts nothing: a field this console
 * declares as required is a field it will happily read as `undefined` at
 * runtime and render as the string "undefined".
 *
 * Marked below: the four fields Go really does omit, and why. The `omitempty`
 * on the two duration fields is the subtle one — it drops a legitimate zero, so
 * "absent" there means "less than a second", not "not applicable".
 */
export interface SLASubjectView {
  readonly customer_id?: number
  readonly state?: string
  /** breached | due_soon | on_track | no_target. */
  readonly posture?: string
  /** customer | institution | nobody — whether chasing will help. */
  readonly waiting_on?: string
  /** The operator-facing sentence, carried so there is one wording of it. */
  readonly blocking?: string
  readonly state_entered_at?: string
  /** Go `omitempty` on a pointer: absent means no SLA target applies. */
  readonly due_at?: string
  /** Go `omitempty` on an int: absent when zero, i.e. under one second. */
  readonly overdue_seconds?: number
  /** Go `omitempty` on an int: absent when zero, i.e. under one second. */
  readonly remaining_seconds?: number
  readonly in_state_seconds?: number
  readonly needs_a_human?: boolean
}

export interface SLAQueueResponse {
  /** The whole breached population, not the length of this page. */
  readonly breached?: number
  readonly subjects?: readonly SLASubjectView[]
}

export interface CohortView {
  /** The month the subjects ENTERED, as YYYY-MM. */
  readonly cohort?: string
  /** The sample size. Without it a cohort of one reads as a trend. */
  readonly verdicts?: number
  readonly p50_seconds?: number
  readonly p90_seconds?: number
}

export interface CohortsResponse {
  readonly cohorts?: readonly CohortView[]
}

export const SLA_QUEUE_PATH = '/onboarding/sla'
export const SLA_COHORTS_PATH = '/onboarding/sla/cohorts'

const SOURCE = 'postgres · onboarding SLA (GET /v1/admin/onboarding/sla + /sla/cohorts)'

/**
 * One endpoint's answer, or the account of why there is none.
 *
 * Compose takes these rather than parsed payloads so that the interesting half
 * of this file — what the screen does when one of two reads failed — is
 * table-testable with no client and no mocked rejection.
 */
export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      /** Never synthesised. `null` means the backend sent none. */
      readonly decisionRef: string | null
      /** A 403. Distinguished so the note can say a refusal came without a ref. */
      readonly refused: boolean
    }

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/**
 * `Array.isArray` narrows `unknown` to `any[]`, which then spreads `any`
 * through everything it touches. Landing it back on `readonly unknown[]` here
 * keeps every element a thing the parse has to prove something about.
 */
function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * Whether a vocabulary this console owns really contains a word off the wire.
 *
 * `word in TABLE` walks the prototype chain, so `constructor`, `toString`,
 * `valueOf`, `hasOwnProperty` and `__proto__` all answer true against any object
 * literal — and the lookup that follows returns a FUNCTION, not a tone. An
 * unrecognised posture would come back "recognised", chip itself with
 * `Object.prototype.toString` in place of a tone, and record no gap, so the
 * screen would badge itself live over a word it does not know. Same guard, and
 * the same reason, as `reconcilerCard` in `dashboard.ts`.
 */
function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

/**
 * An integer we can state exactly, or the reason we cannot.
 *
 * Three states, not two, because absent and unusable are different facts about
 * different bugs: `overdue_seconds` is legitimately absent on a row that is not
 * overdue, and a `customer_id` past `Number.MAX_SAFE_INTEGER` has already lost
 * digits in `JSON.parse` — printing it would show an id that is wrong in its
 * last places and looks entirely plausible, which is how an operator opens the
 * wrong customer.
 */
type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

function readInt(o: Record<string, unknown>, key: string): IntRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

/**
 * A duration in seconds. A negative one is unreadable rather than clamped.
 *
 * Clamping to zero is the tempting move and it is a fabricated zero: a negative
 * elapsed time means the clocks disagree, and "0s in state" tells the operator
 * the customer just arrived. Two different incidents, one of them invisible.
 */
function readDuration(o: Record<string, unknown>, key: string): IntRead {
  const read = readInt(o, key)
  return read.kind === 'exact' && read.value < 0 ? { kind: 'unreadable' } : read
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is not paranoia. `due_at` is a `*time.Time` and is omitted
 * when there is no target, but `state_entered_at` is a plain `time.Time` with
 * no `omitempty` — so a zero value reaches the wire as `0001-01-01T00:00:00Z`
 * and parses perfectly well into a date in the year 1. That renders as a
 * timestamp rather than as an absence, which is the exact rendering the backend
 * went to the trouble of a pointer to avoid on the other field.
 */
function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/**
 * A span, from the server's own arithmetic. No ambient clock here.
 *
 * Truncated, never rounded up, and minutes are kept until two days out — an SLA
 * overrun displayed as "5h" when it is 4h 01m overstates it, and one displayed
 * as "4h" when it is 4h 59m understates it. Past two days the minutes are noise
 * and the days are what gets escalated.
 */
function spanOf(seconds: number): string {
  const s = Math.trunc(seconds)
  if (s < 60) return `${String(s)}s`
  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${String(minutes)}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${String(hours)}h ${String(minutes % 60).padStart(2, '0')}m`
  return `${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`
}

// ---------------------------------------------------------------------------
// One queue row
// ---------------------------------------------------------------------------

/**
 * The onboarding states this console knows, from `internal/onboarding`.
 *
 * Kept only to decide the chip tone. A state added to the backend later renders
 * with the value it sent and an attention tone, which is the less-confident
 * reading — the alternative, a neutral chip, would make a state nobody has
 * taught this console about look like business as usual.
 */
const KNOWN_STATES = new Set(['pending', 'in_review', 'more_info_required', 'accepted', 'rejected'])

/**
 * Posture tone.
 *
 * `on_track` is neutral rather than green: the queue is a work list, and green
 * on a row reads as "resolved" when the application is merely not late yet.
 *
 * `no_target` is attention rather than neutral, which is the judgement call on
 * this table. The backend keeps it distinct from on-track precisely because "we
 * promised nothing here" is not "we promised something and are comfortably
 * inside it"; rendering the first as calm reports SLA coverage we do not have.
 */
const POSTURE_TONE: Readonly<Record<string, Tone>> = {
  breached: 'r',
  due_soon: 'a',
  on_track: 'n',
  no_target: 'a',
}

const POSTURE_LABEL: Readonly<Record<string, string>> = {
  breached: 'breached',
  due_soon: 'due soon',
  on_track: 'on track',
  no_target: 'no target',
}

const WAITING_LABEL: Readonly<Record<string, string>> = {
  customer: 'the customer',
  institution: 'the institution',
  nobody: 'nobody',
}

/**
 * The columns, drawn from what the endpoint can actually answer.
 *
 * The corpus draws Breach / Customer / State / Target / Elapsed / Over by /
 * Owner / Escalated. Three of those have no field on this wire — there is no
 * breach-row id (the queue reads the live due time, not
 * `onboarding_sla_breaches`), no owner and no escalation — so they are gone
 * rather than filled with a plausible "unassigned".
 *
 * Two of them are new and are the point of the endpoint: `Waiting on` says
 * whether chasing will help, and `Blocking` says what the row is waiting for in
 * the backend's own wording.
 *
 * `Over by` and `Left` are two columns because they are two fields. Merging
 * them into one signed column is exactly what the backend split them to
 * prevent: "overdue by three days" and "due in three days" must not be
 * renderable from the same number with a wrong sign.
 */
const QUEUE_COLS: readonly Column[] = [
  ['Customer', 'm', '96px'],
  ['State', 'c', '154px'],
  ['Waiting on', 'c', '124px'],
  ['Posture', 'c', '110px'],
  ['Over by', 'x2', '104px'],
  ['Left', 'x', '92px'],
  ['In state', 's', '140px'],
  ['Due', 'm', '150px'],
  ['Blocking', 'x', 'minmax(220px,1.5fr)'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  /** The normalised party, or `null` when the backend named one we do not know. */
  readonly waiting: string | null
  readonly breached: boolean
  /** What this row could not say, in operator words, one entry per gap. */
  readonly gaps: readonly string[]
}

/**
 * One subject as a row, or `null` when the element is not an object at all.
 *
 * A `null` is counted by the caller and stated on the screen. It is never
 * dropped: a queue that silently renders 4 of 6 rows is a queue two people are
 * not being chased in.
 */
function readSubject(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null

  const gaps: string[] = []

  const id = readInt(o, 'customer_id')
  if (id.kind !== 'exact') gaps.push('a customer id')
  const customer = id.kind === 'exact' ? String(id.value) : '—'

  const state = readText(o, 'state')
  if (state === null) gaps.push('an onboarding state')
  const stateCell: ChipCell = state === null
    ? ['unstated', 'a']
    : [state, KNOWN_STATES.has(state) ? 'n' : 'a']

  const waitingRaw = readText(o, 'waiting_on')
  const waiting = waitingRaw !== null && knows(WAITING_LABEL, waitingRaw) ? waitingRaw : null
  if (waiting === null) gaps.push('a party this console recognises in waiting_on')
  const waitingCell: ChipCell = waiting === null
    ? [waitingRaw ?? 'unstated', 'a']
    : [WAITING_LABEL[waiting] ?? waiting, 'n']

  const postureRaw = readText(o, 'posture')
  const known = postureRaw !== null && knows(POSTURE_TONE, postureRaw)
  if (!known) gaps.push('a posture this console recognises')
  const mapped: Tone = known ? (POSTURE_TONE[postureRaw ?? ''] ?? 'r') : 'r'

  // Two sources for the same judgement, and the backend's flag wins. Today
  // `needs_a_human` is exactly `breached || due_soon`, so the flag agrees with
  // the posture by construction — which is the reason to honour it rather than
  // to drop it, because the day they diverge is the day the posture mapping
  // here is the stale one. One-directional: it can take calm away, never grant
  // it, and a flag that is not a boolean at all does not get to grant it either.
  const human = readBool(o, 'needs_a_human')
  const tone: Tone = (human !== false) && mapped === 'n' ? 'a' : mapped
  const postureCell: ChipCell = [
    known ? (POSTURE_LABEL[postureRaw ?? ''] ?? postureRaw ?? '') : (postureRaw ?? 'unstated'),
    tone,
  ]

  // An empty cell and a dash are different sentences and both are used here.
  // Empty means the field does not apply — a breached row has no time left, and
  // printing one would be a second reading of the same clock. A dash means it
  // applies and could not be read.
  const overdue = readDuration(o, 'overdue_seconds')
  if (overdue.kind === 'unreadable') gaps.push('an overdue figure')
  const overdueCell = overdue.kind === 'exact'
    ? spanOf(overdue.value)
    : overdue.kind === 'unreadable' ? '—' : ''

  const remaining = readDuration(o, 'remaining_seconds')
  if (remaining.kind === 'unreadable') gaps.push('a remaining figure')
  const remainingCell = remaining.kind === 'exact'
    ? spanOf(remaining.value)
    : remaining.kind === 'unreadable' ? '—' : ''

  // In state is the "how long has this person been waiting" figure, and it is
  // NOT how overdue they are — a target can change under a subject. Absent is a
  // dash and never `0s`, because zero is the honest reading for someone who
  // entered the state this second and would be a lie about everyone else.
  const inState = readDuration(o, 'in_state_seconds')
  if (inState.kind !== 'exact') gaps.push('a time in state')
  // Not only `unreadable`. `state_entered_at` is a plain `time.Time` with no
  // `omitempty` and no pointer, so Go sends it on every row: an absent one is an
  // anomaly, not a "does not apply" the way an absent `due_at` is. Letting the
  // "since" line disappear without recording the gap drops a fact silently and
  // leaves the badge saying the screen read everything.
  const entered = readInstant(o, 'state_entered_at')
  if (entered.kind !== 'at') gaps.push('a state-entered timestamp')
  const inStateCell: Cell = entered.kind === 'at'
    ? [inState.kind === 'exact' ? spanOf(inState.value) : '—', `since ${entered.label}`]
    : [inState.kind === 'exact' ? spanOf(inState.value) : '—']

  // Absent `due_at` is a fact, not a gap: the backend omits it precisely when no
  // SLA target applies, and it uses a pointer to do so rather than let a zero
  // time render as the year 1. So it reads as "no target", never as a dash.
  const due = readInstant(o, 'due_at')
  if (due.kind === 'unreadable') gaps.push('a due date')
  const dueCell = due.kind === 'at' ? due.label : due.kind === 'absent' ? 'no target' : '—'

  const blocking = readText(o, 'blocking')
  if (blocking === null) gaps.push('the sentence saying what the row waits for')

  return {
    cells: [
      customer, stateCell, waitingCell, postureCell,
      overdueCell, remainingCell, inStateCell, dueCell,
      blocking ?? '—',
    ],
    waiting,
    breached: postureRaw === 'breached',
    gaps,
  }
}

// ---------------------------------------------------------------------------
// Composition — the queue half
// ---------------------------------------------------------------------------

interface QueueRender {
  readonly blocks: readonly Block[]
  readonly missing: readonly string[]
}

/** Groups repeated gaps so the provenance chip stays readable at 40 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

function queueFooter(rows: number, unreadableRows: number, breached: IntRead): string {
  // Zero rendered rows is two different sentences. An empty page really is "no
  // subject is breached or approaching"; a page whose every element this console
  // failed to read is an unread queue, and saying "no subject is breached" over
  // it is a fabricated all-clear on the one screen an operator scans to decide
  // whether anyone is stuck.
  const shown = rows === 0
    ? (unreadableRows === 0
        ? 'No subject is breached or approaching its target'
        : 'No row on this page could be read')
    : `${String(rows)} ${rows === 1 ? 'subject' : 'subjects'} shown`
  // Never `0 breached`: the count and the page are two reads, and a population
  // we could not read is not a population of none.
  const population = breached.kind === 'exact'
    ? `${String(breached.value)} breached in the whole population`
    : 'the breached population could not be read'
  const lost = unreadableRows === 0
    ? ''
    : ` · ${String(unreadableRows)} row(s) could not be read at all`
  return `${shown} · ${population}${lost}`
}

function handoffNote(rows: readonly RowRead[], unreadableRows: number): NoteBlock {
  const counted = new Map<string, number>()
  for (const row of rows) {
    const key = row.waiting ?? 'unattributed'
    counted.set(key, (counted.get(key) ?? 0) + 1)
  }
  const unattributed = counted.get('unattributed') ?? 0

  // "…and this one was read" is only true when there was nothing to read. With
  // rows on the wire that this console could not parse, the same sentence claims
  // an empty queue was confirmed, which is the reading the note exists to
  // prevent one paragraph further down.
  const shape = rows.length === 0
    ? (unreadableRows === 0
        ? 'Nothing is in the queue, so nothing is waiting on anyone — but the column stays because an empty queue and an unread one are different, and this one was read.'
        : `No row on this page could be read, so nobody is attributed to anyone. This is an unread queue, not an empty one — ${String(unreadableRows)} row(s) came back in a shape this console does not know.`)
    : `Of ${String(rows.length)} rows: ${String(counted.get('institution') ?? 0)} waiting on the institution, `
      + `${String(counted.get('customer') ?? 0)} on the customer, ${String(counted.get('nobody') ?? 0)} on nobody.`

  const caveat = unattributed === 0
    ? ''
    : ` ${String(unattributed)} row(s) named a party this console does not know; they show the value the backend sent and are attributed to nobody.`

  return {
    t: 'note',
    tone: 'a',
    kicker: 'Handoff',
    title: 'Some of this clock is not ours',
    body: `${shape} The breach is still shown — the customer is still waiting either way — but the row names the party that owns the next action, so nobody works a queue of rows they cannot move.${caveat}`,
    ref: 'waiting_on · derived from the acceptance state, not from an assignee',
  }
}

function renderQueue(payload: unknown): QueueRender | null {
  const o = asRecord(payload)
  if (o === null) return null
  const subjects = asArray(o['subjects'])
  if (subjects === null) return null

  const breached = readInt(o, 'breached')
  const rows: RowRead[] = []
  let unreadableRows = 0
  for (const raw of subjects) {
    // The order is the backend's severity ranking (worst-first, longest-overdue
    // within breached). Re-sorting here would silently disagree with the
    // ordering the endpoint documents and tests.
    const row = readSubject(raw)
    if (row === null) unreadableRows += 1
    else rows.push(row)
  }

  const gaps = summarise(rows.flatMap((r) => r.gaps))
  const missing = [...gaps]
  if (breached.kind !== 'exact') missing.push('the breached population count')
  if (unreadableRows > 0) missing.push(`${String(unreadableRows)} queue row(s) this console could not read`)

  const breachedShown = rows.filter((r) => r.breached).length
  const truncated = breached.kind === 'exact' && breached.value > breachedShown

  const table: TableBlock = {
    t: 'table',
    title: 'Breached and approaching subjects',
    note: 'Ordered worst-first by the backend; this console does not re-sort.',
    // The corpus draws facets (`state=in_review`, `breach_age>24h`). This read
    // applies no filter at all, and a facet bar is a claim that one is applied.
    facets: false,
    cols: QUEUE_COLS,
    rows: rows.map((r) => r.cells),
    footer: queueFooter(rows.length, unreadableRows, breached),
    foot2: truncated
      ? 'This page is shorter than the breached population — the backend caps the page, so this is not the whole queue. Nothing here approves anything: only the institution accepts or rejects.'
      : 'Nothing here approves anything: only the institution accepts or rejects.',
  }

  const blocks: Block[] = [table]

  if (gaps.length > 0 || unreadableRows > 0) {
    const lost = unreadableRows === 0
      ? []
      : [`${String(unreadableRows)} row(s) were not objects this console could read and are not shown as rows at all.`]
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not shown',
      title: 'Some fields on this queue could not be read',
      body: [...(gaps.length === 0 ? [] : [gaps.join(' · ')]), ...lost].join(' '),
      ref: 'A field that could not be read is not a field of zero.',
    })
  }

  blocks.push(handoffNote(rows, unreadableRows))
  return { blocks, missing }
}

// ---------------------------------------------------------------------------
// Composition — the trend half
// ---------------------------------------------------------------------------

const COHORT_COLS: readonly Column[] = [
  ['Cohort', 'm', '110px'],
  ['Verdicts', 'n', '96px'],
  ['Median to verdict', 'm', '170px'],
  ['p90 to verdict', 'm', '170px'],
]

/**
 * The trend as a table, not as a chart.
 *
 * A `chart` block would draw four bars and have nowhere to put the sample size,
 * and the sample size is the whole defence against a cohort of one reading as a
 * trend — which is why the backend carries `verdicts` beside the percentiles at
 * all. A bar that is short because one person was accepted in an hour looks
 * exactly like a month of fast decisions.
 */
function renderCohorts(payload: unknown): { readonly block: Block; readonly missing: readonly string[] } | null {
  const o = asRecord(payload)
  if (o === null) return null
  const raw = asArray(o['cohorts'])
  if (raw === null) return null

  const rows: (readonly Cell[])[] = []
  const gaps: string[] = []
  let unreadableRows = 0

  for (const entry of raw) {
    const c = asRecord(entry)
    if (c === null) {
      unreadableRows += 1
      continue
    }
    const cohort = readText(c, 'cohort')
    if (cohort === null) gaps.push('a cohort month')

    const verdicts = readInt(c, 'verdicts')
    const p50 = readDuration(c, 'p50_seconds')
    const p90 = readDuration(c, 'p90_seconds')

    // A percentile with no sample size beside it is the figure this endpoint
    // exists to avoid publishing, so an unreadable count withholds both
    // percentiles rather than showing them unqualified. The alternative — print
    // them and dash the count — puts a number on screen that an operator has no
    // way to weigh, which is the cohort-of-one failure with extra steps.
    // Absent counts here as well as unreadable. Neither percentile carries
    // `omitempty` in Go, so an absent one is an anomaly rather than a "does not
    // apply" — and the cell renders a dash either way. A dash the provenance
    // chip does not know about is a figure withheld from a screen still badging
    // itself live, which is the defect the dashboard adapter shipped with.
    const unqualified = verdicts.kind !== 'exact'
    if (unqualified) gaps.push('a cohort sample size, so its percentiles are withheld')
    if (!unqualified && p50.kind !== 'exact') gaps.push('a median time to verdict')
    if (!unqualified && p90.kind !== 'exact') gaps.push('a p90 time to verdict')

    rows.push([
      cohort ?? '—',
      verdicts.kind === 'exact' ? String(verdicts.value) : '—',
      unqualified || p50.kind !== 'exact' ? '—' : spanOf(p50.value),
      unqualified || p90.kind !== 'exact' ? '—' : spanOf(p90.value),
    ])
  }

  // Counted, never summarised: `summarise` appends "(n rows)" to each entry it
  // is given, and this line is already a count — it read "1 cohort row(s) this
  // console could not read (1 row)".
  const missing = summarise(gaps)
  if (unreadableRows > 0) {
    missing.push(`${String(unreadableRows)} cohort row(s) this console could not read`)
  }

  const block: TableBlock = {
    t: 'table',
    title: 'Time to verdict, by entry cohort',
    facets: false,
    cols: COHORT_COLS,
    rows,
    footer: rows.length === 0
      // An empty list is a read, and it says something quite specific. Left to
      // read as a blank table it would say nothing at all, which is how a
      // failed half of a screen hides inside a working one. A window whose every
      // row was unreadable is the other sentence entirely, and claiming "nobody
      // reached a verdict in a year" over it is worse than the blank table.
      ? (unreadableRows === 0
          ? 'No application reached a verdict in the window the backend returned. This is a read of an empty window, not a failed read.'
          : `No cohort row could be read: ${String(unreadableRows)} row(s) came back in a shape this console does not know. This is an unread window, not an empty one.`)
      : `${String(rows.length)} ${rows.length === 1 ? 'cohort' : 'cohorts'} · newest first`,
    foot2: 'Cohorted on the month the application entered, not the month it finished — a cohort keyed on completion leaves the still-stuck applications out of its own number. Accepted and rejected both count, because measuring only approvals would let this figure improve by refusing people.',
  }

  return { block, missing }
}

function cohortsUnread(reason: string, ref: string): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Time to verdict',
    title: 'The trend could not be read',
    body: `${reason} The queue above was read and is real; this half of the screen was not. Nothing here says whether we are getting faster or slower — including that we are not.`,
    ref,
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * How a refusal is quoted, and what is said when it cannot be.
 *
 * Law 13 — every refusal surfaces a `decision_ref`. A refusal that arrives
 * without one leaves an operator with nothing to put in an escalation, and the
 * console says exactly that rather than inventing a reference or staying quiet
 * about the gap.
 */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

/**
 * The screen when the queue itself could not be read.
 *
 * One note and nothing else — not a corpus row, not a corpus count, not the
 * trend table even when the trend read succeeded. A trend rendered above an
 * absent queue reads as a screen with a quiet day behind it, and the operator's
 * next action after "no breaches today" is to look somewhere else.
 */
function unreadableScreen(failed: Extract<Attempt, { ok: false }>, trendRead: boolean): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'SLA breaches',
      title: 'This screen could not be read',
      body: `${failed.reason} No subject, posture or elapsed time below is known — that is different from nobody being stuck, and the screen shows none rather than guessing at any.${
        trendRead
          ? ' The cohort trend did answer and is withheld anyway: a trend with no queue under it reads as a healthy screen.'
          : ''
      }`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: {
      kind: 'partial',
      source: SOURCE,
      // `unattributed`, not `operational`, and for the same reason as the empty
      // stamp below: authority names who the figures on screen are answerable
      // to, and there are none. `dashboard.ts` says the same thing in the same
      // situation.
      authority: 'unattributed',
      // Nothing was read, so nothing is stamped. Dating the attempt with the
      // browser's clock would put a freshness on figures that do not exist.
      readAt: '',
      missing: trendRead
        ? ['the SLA breach queue', 'the cohort trend (withheld — never shown without the queue)']
        : ['the SLA breach queue', 'the cohort trend'],
    },
  }
}

/**
 * Folds both reads into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every absence path — including the one
 * that matters most, a queue that read and a trend that did not — is
 * table-testable without a client (CLAUDE.md Part III).
 *
 * `at` is the instant the console received the answers. It is a parameter
 * because a pure function does not read a clock, and it is labelled as a
 * receipt on screen because neither endpoint says when it read its rows.
 */
export function composeOnboardingSLA(queue: Attempt, cohorts: Attempt, at: Date): ScreenData {
  if (!queue.ok) return unreadableScreen(queue, cohorts.ok)

  const rendered = renderQueue(queue.payload)
  if (rendered === null) {
    return unreadableScreen({
      ok: false,
      reason: 'The onboarding SLA queue returned a shape this console does not know.',
      decisionRef: null,
      refused: false,
    }, cohorts.ok)
  }

  const blocks: Block[] = [...rendered.blocks]
  const missing: string[] = [...rendered.missing]

  const trend = cohorts.ok ? renderCohorts(cohorts.payload) : null
  if (trend === null) {
    const failure: Extract<Attempt, { ok: false }> = cohorts.ok
      ? {
          ok: false,
          reason: 'The cohort endpoint returned a shape this console does not know.',
          decisionRef: null,
          refused: false,
        }
      : cohorts
    blocks.push(cohortsUnread(failure.reason, refLine(failure, 'Nothing from this endpoint was read.')))
    missing.push('the cohort trend (could not be read)')
  } else {
    blocks.push(trend.block)
    missing.push(...trend.missing)
  }

  // `live` requires both halves. A screen that read the queue and lost the
  // trend has been read in part, and the badge is the only place that says so
  // once the trend note has scrolled off.
  const readAt = `${readAtStamp(at)} (console receipt — neither endpoint states when it read)`
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: SOURCE, authority: 'operational', readAt }
    : { kind: 'partial', source: SOURCE, authority: 'operational', readAt, missing }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * One read, resolved into an answer either way.
 *
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it here would render "the queue could not be
 * read" over a screen the operator has already left.
 */
async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return { ok: false, reason: `${subject} could not be reached.`, decisionRef: null, refused: false }
  }
}

/**
 * Reads the onboarding SLA queue and the verdict trend.
 *
 * Both in parallel: they are independent reads and the queue is the one an
 * operator is waiting on, so making it queue behind a percentile query would
 * cost the screen its slowest endpoint twice.
 *
 * Resolves rather than throwing, per the loader contract. A backend that said
 * "I don't know" is a `partial` the operator can read, not an exception that
 * collapses the screen to a stack trace.
 */
export const loadOnboardingSLA: ScreenLoader = async (api) => {
  const [queue, cohorts] = await Promise.all([
    attempt(() => api.get<unknown>(SLA_QUEUE_PATH), 'The onboarding SLA queue'),
    attempt(() => api.get<unknown>(SLA_COHORTS_PATH), 'The cohort timings'),
  ])
  return composeOnboardingSLA(queue, cohorts, new Date())
}
